import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { SynergyMapping } from '../../types/ScenarioManagement';
import { StandardCard, StandardButton } from '../ui';
import { investments as globalInvestments } from '../../data/investments';
import { incidents as globalIncidents } from '../../data/incidents';
import { UnifiedScoringService } from '../../services/UnifiedScoringService';
import { IncidentResponse } from '../../types/Incident';

interface SynergyTestPanelProps {
  synergies: SynergyMapping[];
  onClose: () => void;
}

export const SynergyTestPanel: React.FC<SynergyTestPanelProps> = ({
  synergies,
  onClose
}) => {
  const { isDark } = useTheme();
  const scoringService = UnifiedScoringService.getInstance();
  
  const [selectedInvestmentIds, setSelectedInvestmentIds] = useState<string[]>([]);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string>('incident-1');
  const [selectedResponseId, setSelectedResponseId] = useState<string>('option-1');
  
  // Get selected investments
  const selectedInvestments = globalInvestments.filter(inv => 
    selectedInvestmentIds.includes(inv.id)
  );
  
  // Get selected incident and response
  const selectedIncident = globalIncidents.find(inc => inc.id === selectedIncidentId);
  const selectedResponse = selectedIncident?.responseOptions.find(opt => opt.id === selectedResponseId);
  
  // Calculate which synergies are active
  const getActiveSynergies = () => {
    return synergies.filter(synergy => {
      // Check if all required investments are selected
      const hasAllInvestments = synergy.requiredInvestmentIds.every(id => 
        selectedInvestmentIds.includes(id)
      );
      
      if (!hasAllInvestments) return false;
      
      // Check custom condition if present
      if (synergy.conditions?.customCondition && selectedResponse) {
        const condition = synergy.conditions.customCondition.toLowerCase();
        const responseLabel = selectedResponse.label.toLowerCase();
        
        // Simple keyword matching
        if (condition.includes('contains')) {
          const keywords = condition.split('contains')[1]?.trim().replace(/['"]/g, '').split('|');
          return keywords?.some(keyword => responseLabel.includes(keyword.trim()));
        }
      }
      
      return true;
    });
  };
  
  const activeSynergies = getActiveSynergies();
  
  // Calculate score preview
  const calculatePreview = () => {
    if (!selectedIncident || !selectedResponse) return null;
    
    // Create mock incident response
    const mockResponse: IncidentResponse = {
      incidentId: selectedIncidentId,
      selectedOptionId: selectedResponseId,
      responseTime: 60,
      timestamp: new Date()
    };
    
    // Calculate with and without synergies
    const scoreWithSynergies = scoringService.calculateScore(
      selectedInvestments,
      [mockResponse],
      [selectedIncident],
      synergies
    );
    
    const scoreWithoutSynergies = scoringService.calculateScore(
      selectedInvestments,
      [mockResponse],
      [selectedIncident],
      []
    );
    
    return {
      withSynergies: scoreWithSynergies,
      withoutSynergies: scoreWithoutSynergies
    };
  };
  
  const preview = calculatePreview();
  
  const getCategoryIcon = (category: string): string => {
    switch (category) {
      case 'security': return '🛡️';
      case 'reputation': return '⭐';
      case 'financial': return '💰';
      case 'operational': return '⚙️';
      default: return '📊';
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <StandardCard variant="outlined" padding="large">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Test Synergies
            </h3>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Select investments and a response to see how synergies affect scoring
            </p>
          </div>
          <StandardButton
            onClick={onClose}
            variant="secondary"
            size="small"
          >
            Close
          </StandardButton>
        </div>
      </StandardCard>

      {/* Investment Selection */}
      <StandardCard variant="outlined" padding="large">
        <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Select Investments
        </h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {globalInvestments.map(investment => (
            <label key={investment.id} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedInvestmentIds.includes(investment.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedInvestmentIds([...selectedInvestmentIds, investment.id]);
                  } else {
                    setSelectedInvestmentIds(selectedInvestmentIds.filter(id => id !== investment.id));
                  }
                }}
                className="w-4 h-4 text-professional-blue-600"
              />
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {investment.icon} {investment.name}
              </span>
            </label>
          ))}
        </div>
      </StandardCard>

      {/* Incident and Response Selection */}
      <StandardCard variant="outlined" padding="large">
        <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Select Incident and Response
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Incident
            </label>
            <select
              value={selectedIncidentId}
              onChange={(e) => {
                setSelectedIncidentId(e.target.value);
                const incident = globalIncidents.find(inc => inc.id === e.target.value);
                if (incident && incident.responseOptions.length > 0) {
                  setSelectedResponseId(incident.responseOptions[0].id);
                }
              }}
              className={`w-full px-3 py-2 rounded-lg border ${
                isDark
                  ? 'bg-gray-800 border-gray-700 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              {globalIncidents.map(incident => (
                <option key={incident.id} value={incident.id}>
                  {incident.title}
                </option>
              ))}
            </select>
          </div>

          {selectedIncident && (
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Response Option
              </label>
              <select
                value={selectedResponseId}
                onChange={(e) => setSelectedResponseId(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDark
                    ? 'bg-gray-800 border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                {selectedIncident.responseOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </StandardCard>

      {/* Active Synergies */}
      <StandardCard variant="elevated" padding="large">
        <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Active Synergies ({activeSynergies.length})
        </h4>
        
        {activeSynergies.length === 0 ? (
          <p className={`text-center py-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            No synergies are currently active. Select investments that work together.
          </p>
        ) : (
          <div className="space-y-3">
            {activeSynergies.map(synergy => (
              <div
                key={synergy.id}
                className={`p-4 rounded-lg ${
                  isDark ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'
                }`}
              >
                <h5 className={`font-medium mb-1 ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                  {synergy.name}
                </h5>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {synergy.description}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.entries(synergy.effects.responseBonuses || {}).map(([category, bonus]) => {
                    if (bonus === 0) return null;
                    return (
                      <span
                        key={category}
                        className={`text-xs px-2 py-1 rounded-full ${
                          isDark ? 'bg-gray-800' : 'bg-gray-100'
                        }`}
                      >
                        {getCategoryIcon(category)} +{bonus}%
                      </span>
                    );
                  })}
                  {(synergy.effects.impactMitigation || 0) > 0 && (
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        isDark ? 'bg-gray-800' : 'bg-gray-100'
                      }`}
                    >
                      🛡️ Mitigation +{synergy.effects.impactMitigation}%
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </StandardCard>

      {/* Score Comparison */}
      {preview && (
        <StandardCard variant="elevated" padding="large">
          <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Score Impact
          </h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div className={`text-center p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Without Synergies
              </p>
              <p className={`text-3xl font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {Math.round(preview.withoutSynergies.overallScore)}
              </p>
            </div>
            
            <div className={`text-center p-4 rounded-lg ${
              isDark ? 'bg-green-500/20 border border-green-500/30' : 'bg-green-50 border border-green-200'
            }`}>
              <p className={`text-sm font-medium mb-2 ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                With Synergies
              </p>
              <p className={`text-3xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                {Math.round(preview.withSynergies.overallScore)}
              </p>
              {preview.withSynergies.overallScore > preview.withoutSynergies.overallScore && (
                <p className={`text-sm mt-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                  +{Math.round(preview.withSynergies.overallScore - preview.withoutSynergies.overallScore)} bonus
                </p>
              )}
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="mt-6 space-y-2">
            <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Category Impact
            </p>
            {(['security', 'reputation', 'financial', 'operational'] as const).map(category => {
              const withSyn = preview.withSynergies.finalScore[category];
              const withoutSyn = preview.withoutSynergies.finalScore[category];
              const diff = withSyn - withoutSyn;
              
              return (
                <div key={category} className="flex items-center justify-between">
                  <span className={`text-sm capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {getCategoryIcon(category)} {category}
                  </span>
                  <div className="flex items-center gap-4">
                    <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {Math.round(withoutSyn)}
                    </span>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-400'}`}>
                      →
                    </span>
                    <span className={`text-sm font-medium ${
                      diff > 0 
                        ? isDark ? 'text-green-400' : 'text-green-600'
                        : isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {Math.round(withSyn)} {diff > 0 && `(+${Math.round(diff)})`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </StandardCard>
      )}
    </div>
  );
};